import { Info, TriangleAlert } from "lucide-react";
import { useId, useRef, useState } from "react";

import {
  stepRoleCapturedBy,
  type ScreeningDiagnosisDocument,
} from "@/lib/screeningDocuments";
import {
  criteriaAreComplete,
  effectiveCount,
  effectiveCriteria,
  entryEffects,
  inheritedIsKnown,
  ORIGIN,
  type EffectiveCriterion,
  type RuleOrigin,
  type ScreeningRuleContext,
} from "@/lib/screeningSemantics";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Label, Select } from "@/ui/Input";

/**
 * `<slug>_screening_diagnosis.yaml` as named controls that state what each rule
 * DOES (#912, corrected here).
 *
 * ## What was wrong, and why it mattered clinically
 *
 * The first version of this form put three sentences on screen that the pinned
 * engine does not agree with. Each of them made a rule read as weaker than it is:
 *
 *   1. *"Leave empty for no per-step requirement."* An absent `mandatory_roles`
 *      entry is not an absent requirement — `diagnose_logic_process` defaults it
 *      to `total_sub_steps`, the number of classifier answers in that criterion.
 *      Empty means **all of them**. Base canon's PTSD omits `step3` and NII adds
 *      `step3: 999`; on the old wording those read as "no requirement" and "an
 *      impossible requirement", when the real delta is *one answer* versus
 *      *never*.
 *   2. *"Counts toward the yes total."* `include_mandatory_yeses` is read twice.
 *      A `true` flag also makes the whole screening fail when that criterion has
 *      zero positive answers — the half MOD's PTSD source turns off on purpose,
 *      in a comment that says so. On the old label, `true` and *not set* looked
 *      like the same answer.
 *   3. Rules could be addressed to any step in the union of the CONVERSATION
 *      steps and the classifier's fields. Those are two different vocabularies:
 *      mania asks four questions and classifies three, because the terminal
 *      `<DONE_TOKEN>` step is never classified. A rule on `step4` saved happily
 *      and did nothing, for the life of the pack.
 *
 * ## What it shows now
 *
 * The rows are the DIAGNOSTIC CRITERIA — the classifier's own fields, published
 * by `GET /library/assets/{id}/screening-rules` — and each one states its
 * effective value and which layer supplies it: set here, inherited from base
 * canon (the overlay merge is `dict ⊕ dict`, so an omitted entry inherits rather
 * than clears), or the engine's own default. Clearing a control says what the
 * criterion will fall back to, from the same source rather than from an
 * assumption.
 *
 * Anything the document names that is NOT a criterion is kept, shown, and
 * explained as having no effect. Nothing is deleted or rewritten: these are
 * immutable clinical documents, and a form that quietly dropped an inert rule
 * would be editing content the author never chose to edit. What the service
 * refuses is a NEW or CHANGED rule at such a target, and this form refuses it in
 * the same place for the same reason.
 *
 * ## Absent, zero and false are still three different answers
 *
 * Unchanged from the first version and still the design constraint. An empty
 * control writes no key — never `null`, which the schema refuses and which would
 * reach `mandatory_roles_met >= None` and raise mid-conversation. `0` is a value
 * (`mandatory_count: 0` is what routes MOD's negative PTSD screen to full
 * evaluation). `false` is a value, which is why the flag is a three-option
 * `Select` and not a checkbox.
 *
 * ## No arithmetic is invented here
 *
 * S7 contract §5. Nothing relates `yes_count` to `mandatory_count` or to the
 * number of steps; `999` has no upper bound and is reported as unreachable
 * rather than refused. Every consequence this form states comes from
 * `lib/screeningSemantics`, which is pinned to the service's own module, which
 * is pinned to the pinned engine.
 *
 * ## When the context cannot be resolved
 *
 * The criteria come from the classification schema, which is frequently base
 * canon's rather than this version's. Where that could not be read — no mounted
 * base pack, no derivable coordinate, nothing shipped at one — this form says
 * so and describes NO effective value. It keeps the controls usable, because the
 * document is still authorable and the service still decides; what it will not
 * do is claim a rule was verified.
 */
export function ScreeningDiagnosisForm({
  document,
  declaredStepRoles,
  context,
  contextPending,
  contextFailed,
  onRetryContext,
  readOnly,
  onChange,
}: {
  document: ScreeningDiagnosisDocument;
  /**
   * The `stepN` names this version declares, or `null` when it declares none.
   *
   * Still the SERVICE'S REFERENCE RULE (`_declared_step_roles`), and still
   * needed: it is what decides the "not a step in this screening" refusal and
   * the prefix-collision refusal, both of which apply to every mapping key
   * whether or not a classifier field exists for it.
   */
  declaredStepRoles: ReadonlySet<string> | null;
  /**
   * The classification criteria and base canon's own document, from the
   * service's projection.
   *
   * `criteria: null` means unresolved, and this form then states the refusal
   * rather than an effective value.
   */
  context: ScreeningRuleContext;
  /** The projection has not been read yet — a different state from unresolved. */
  contextPending: boolean;
  /**
   * The projection READ failed, rather than answering that a half is unresolved.
   *
   * A THIRD STATE, not a flavour of the second. "The base pack is not mounted"
   * is the service's own answer about content; "the request did not arrive" is a
   * transport fact this surface can offer to retry. Rendering the second as the
   * first produced the worst sentence available — a content refusal with no
   * reason — which is what this prop exists to stop.
   */
  contextFailed: boolean;
  /** Absent when retrying would only repeat a terminal refusal. */
  onRetryContext?: () => void;
  readOnly: boolean;
  onChange: (next: ScreeningDiagnosisDocument) => void;
}) {
  const mandatoryRoles = document.mandatory_roles;
  const includeYeses = document.include_mandatory_yeses;

  const criteria = effectiveCriteria(document, context);
  const complete = criteriaAreComplete(context);
  const effects = entryEffects(document, context);
  const yesCount = effectiveCount("yes_count", document, context, null);
  const mandatoryCount = effectiveCount("mandatory_count", document, context, 0);
  /**
   * Whether "neither layer sets this" is a conclusion or a guess.
   *
   * `lib/screeningSemantics`' own predicate rather than a second copy: the same
   * fact decides whether the derivation reports an effective value at all, and
   * two spellings of it would let this form promise a fallback the values
   * beside it refuse to name.
   */
  const inheritedKnown = inheritedIsKnown(context);

  /**
   * Every `stepN` the document addresses that is NOT one of the criteria.
   *
   * KEPT AND EXPLAINED, NEVER HIDDEN. These are stored clinical values, and a
   * form that showed only the criteria would make an inert rule invisible while
   * it stayed in every export. Ordered by mapping and then by step number so the
   * list reads the same way twice.
   */
  const criterionTargets = new Set((criteria ?? []).map((rule) => rule.target));
  const otherTargets =
    criteria === null
      ? // EMPTY WHILE THE CRITERIA ARE UNRESOLVED, because the fallback rows
        // below already render every key the document names. Without this the
        // same step appeared twice on one screen — once as an editable row and
        // once as an entry "addressed to something else" — which is two
        // different things to say about one value.
        []
      : [
          ...new Set([
            ...Object.keys(mandatoryRoles ?? {}),
            ...Object.keys(includeYeses ?? {}),
          ]),
        ]
          .filter((role) => !criterionTargets.has(role))
          .sort(compareStepRoles);

  /**
   * The rows to render when the criteria could not be resolved.
   *
   * THE OLD VOCABULARY, deliberately, and only here. With no classifier to
   * resolve against, the honest set is the one the service's own reference check
   * uses — the version's declared steps plus whatever the document names — and
   * the banner above says no effective value is being claimed for any of them.
   * Falling back to an empty list would hide authored content behind a
   * deployment condition.
   */
  const unresolvedRows = [
    ...new Set([
      ...(declaredStepRoles ?? []),
      ...Object.keys(mandatoryRoles ?? {}),
      ...Object.keys(includeYeses ?? {}),
    ]),
  ].sort(compareStepRoles);

  /**
   * THE KEY ORDER THIS DOCUMENT ARRIVED IN, captured once per open.
   *
   * `document` is re-parsed from the draft text on every render, so by the time
   * a removed key is put back its original position is gone from the value
   * itself. This ref holds the order the step screen opened with, which is the
   * order the STORED document has — and stored documents are immutable and
   * predate this form, so their order is not guaranteed to be the schema's.
   */
  const openedWith = useRef<Record<string, unknown> | null>(null);
  openedWith.current ??= document as unknown as Record<string, unknown>;
  const openedWithOrder = useRef<string[] | null>(null);
  openedWithOrder.current ??= Object.keys(document);

  /**
   * `source` with `key` set to `value`, placed as `preferred` orders it.
   *
   * An EXISTING key keeps its slot; a returning one is put back where
   * `preferred` says it belongs rather than appended. Used at both levels,
   * because both levels are serialised by `JSON.stringify` in insertion order
   * and both are compared as text when the draft decides it is dirty.
   */
  function placed(
    source: Record<string, unknown>,
    key: string,
    value: unknown,
    preferred: readonly string[],
  ): Record<string, unknown> {
    if (key in source) {
      return { ...source, [key]: value };
    }
    const rebuilt: Record<string, unknown> = {};
    const pending = new Set([...Object.keys(source), key]);
    for (const candidate of preferred) {
      if (pending.delete(candidate)) {
        rebuilt[candidate] = candidate === key ? value : source[candidate];
      }
    }
    for (const candidate of Object.keys(source)) {
      if (pending.delete(candidate)) {
        rebuilt[candidate] = source[candidate];
      }
    }
    if (pending.delete(key)) {
      rebuilt[key] = value;
    }
    return rebuilt;
  }

  /**
   * THE ONE WRITER for every field of this document, so that key ORDER is one
   * decision rather than four.
   *
   * Clearing removes the key; setting an existing key replaces it in place; and
   * re-adding a key the document has lost puts it back where THIS document
   * carried it, falling back to the schema's order for a key the document never
   * had.
   *
   * The last clause is the whole point. `JSON.stringify` writes keys in
   * insertion order and dirtiness is decided on the draft TEXT, so a plain
   * `{ ...document, [field]: value }` after a delete makes clear-and-retype — an
   * edit that changes no value — report a dirty draft and offer to mint an
   * immutable clinical version whose only delta is the order of its keys.
   */
  function withField(field: string, value: unknown): ScreeningDiagnosisDocument {
    const current: Record<string, unknown> = { ...document };
    if (value === undefined) {
      delete current[field];
      return current as ScreeningDiagnosisDocument;
    }
    return placed(current, field, value, [
      ...(openedWithOrder.current ?? []),
      "yes_count",
      "mandatory_count",
      "mandatory_roles",
      "include_mandatory_yeses",
    ]) as ScreeningDiagnosisDocument;
  }

  /**
   * Set or clear one entry of one mapping.
   *
   * NEVER INVENTS A MAPPING: emptying one removes the key rather than leaving
   * `{}` behind. Absent and `{}` are identical to the engine
   * (`.get(field, {})`), so neither spelling changes what runs — which is what
   * makes "absent" the safe one to converge on.
   *
   * Re-adding an entry restores the mapping to its own place through
   * `withField`, so set → clear → set is byte-identical rather than a document
   * with its keys shuffled.
   */
  function withEntry<Value>(
    field: "mandatory_roles" | "include_mandatory_yeses",
    role: string,
    value: Value | undefined,
  ) {
    const current = (document[field] ?? {}) as Record<string, Value>;
    if (value === undefined) {
      const emptied: Record<string, Value> = { ...current };
      delete emptied[role];
      return withField(
        field,
        Object.keys(emptied).length === 0 ? undefined : emptied,
      );
    }
    // PLACED INSIDE THE MAPPING, not just at the top level. `withField` kept
    // `mandatory_roles` in its own slot while a cleared-and-retyped ENTRY still
    // moved to the end of it — so on the ordinary MOD/NII shape
    // (`{step1, step2, step3}`) clearing `step1` and retyping it produced
    // `{step2, step3, step1}`: a dirty draft and a publishable version whose
    // only delta is nested key order.
    const order = Object.keys(
      (openedWith.current?.[field] ?? {}) as Record<string, unknown>,
    );
    return withField(field, placed(current, role, value, order));
  }

  return (
    <div className="space-y-6">
      <ContextNotice
        context={context}
        pending={contextPending}
        failed={contextFailed}
        {...(onRetryContext ? { onRetry: onRetryContext } : {})}
        criteriaCount={criteria?.length ?? null}
      />

      <section className="space-y-4">
        <h3 className="text-sm font-medium text-foreground">
          Overall thresholds
        </h3>
        <IntegerField
          label="Yes answers required"
          description="How many positive answers across the counted criteria route this screening to full evaluation. Required; zero is a value, and means no positive answers are needed."
          value={document.yes_count}
          required
          readOnly={readOnly}
          effective={
            <EffectiveNote
              // NO ENGINE DEFAULT EXISTS for this field: the pinned loader
              // INDEXES `yes_count`, so a document without one anywhere raises
              // mid-conversation rather than defaulting. That is what the last
              // branch says, and it is why the sentence is not "defaults to 0".
              // BRANCH ON THE ORIGIN, NEVER ON THE VALUE. Coalescing a null
              // effective printed `Effective: null positive answers` and, one
              // field down, a fabricated `Effective: 0` — the exact claim this
              // form exists to withhold, arriving through the representation
              // rather than through the derivation.
              text={countSentence(
                yesCount,
                (value) =>
                  `Effective: ${counted(value, POSITIVE_ANSWERS)}${originSuffix(yesCount.origin)}.`,
                inheritedKnown
                  ? "Neither this version nor the base pack sets this. The clinical engine requires it and would fail the screening without it."
                  : "This version does not set it and the base pack could not be read, so what the clinical engine would use is not known.",
              )}
              tone={yesCount.origin === ORIGIN.authored || yesCount.origin === ORIGIN.inherited ? "muted" : "warning"}
              clearing={
                inheritedKnown
                  ? clearingNote(
                      document.yes_count !== undefined,
                      yesCount.inherited,
                      null,
                      POSITIVE_ANSWERS,
                      yesCount.inheritedUnreadable,
                    )
                  : null
              }
            />
          }
          onChange={(value) => onChange(withField("yes_count", value))}
        />

        <IntegerField
          label="Mandatory criteria required"
          description="How many diagnostic criteria must meet their own requirement. Zero requires none; leaving it empty inherits."
          value={document.mandatory_count}
          readOnly={readOnly}
          effective={
            <EffectiveNote
              text={countSentence(
                mandatoryCount,
                (value) =>
                  `Effective: ${String(value)} of ${counted(criteria?.length ?? 0, CRITERIA)}${originSuffix(mandatoryCount.origin)}.`,
                "This version does not set it and the base pack could not be read, so how many criteria the clinical engine would require is not known.",
              )}
              tone={
                mandatoryCount.origin === ORIGIN.unresolved ||
                mandatoryCount.origin === ORIGIN.unreadable
                  ? "warning"
                  : "muted"
              }
              clearing={
                inheritedKnown
                  ? clearingNote(
                      document.mandatory_count !== undefined,
                      mandatoryCount.inherited,
                      0,
                      CRITERIA,
                      mandatoryCount.inheritedUnreadable,
                    )
                  : null
              }
              // NOT SHOWN AT ALL where the criteria are unresolved: the "of M"
              // has no M, and a sentence about a total nobody knows is worse
              // than none. Every other state now has its own words.
              unavailable={criteria === null}
            />
          }
          onChange={(value) => onChange(withField("mandatory_count", value))}
        />
      </section>

      <section className="space-y-3">
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-foreground">
            Diagnostic criteria
          </h3>
          <VocabularyNote
            criteria={criteria}
            declaredStepRoles={declaredStepRoles}
          />
        </div>
        {criteria === null ? (
          <ul className="space-y-3">
            {unresolvedRows.map((role) => (
              <UnresolvedRequirementRow
                key={role}
                role={role}
                unknown={
                  declaredStepRoles !== null && !declaredStepRoles.has(role)
                }
                capturedByCount={stepRoleCapturedBy(
                  role,
                  declaredStepRoles,
                  "mandatory_roles",
                )}
                capturedByFlag={stepRoleCapturedBy(
                  role,
                  declaredStepRoles,
                  "include_mandatory_yeses",
                )}
                count={mandatoryRoles?.[role]}
                include={includeYeses?.[role]}
                readOnly={readOnly}
                onCount={(value) =>
                  onChange(withEntry("mandatory_roles", role, value))
                }
                onInclude={(value) =>
                  onChange(withEntry("include_mandatory_yeses", role, value))
                }
              />
            ))}
          </ul>
        ) : criteria.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This screening&rsquo;s classification schema declares no fields, so
            there is no criterion a diagnosis rule could address.
          </p>
        ) : (
          <ul className="space-y-3">
            {criteria.map((criterion) => (
              <CriterionRow
                key={criterion.target}
                criterion={criterion}
                inheritedIsKnown={inheritedKnown}
                authoredCount={mandatoryRoles?.[criterion.target]}
                authoredInclude={includeYeses?.[criterion.target]}
                capturedByCount={stepRoleCapturedBy(
                  criterion.target,
                  declaredStepRoles,
                  "mandatory_roles",
                )}
                capturedByFlag={stepRoleCapturedBy(
                  criterion.target,
                  declaredStepRoles,
                  "include_mandatory_yeses",
                )}
                readOnly={readOnly}
                onCount={(value) =>
                  onChange(
                    withEntry("mandatory_roles", criterion.target, value),
                  )
                }
                onInclude={(value) =>
                  onChange(
                    withEntry(
                      "include_mandatory_yeses",
                      criterion.target,
                      value,
                    ),
                  )
                }
              />
            ))}
          </ul>
        )}
      </section>

      {otherTargets.length > 0 ? (
        <InertEntries
          targets={otherTargets}
          mandatoryRoles={mandatoryRoles}
          includeYeses={includeYeses}
          // Only a COMPLETE criteria list can support "no effect". With a layer
          // unread, the honest sentence is that this could not be checked.
          verified={complete}
          effects={effects}
          readOnly={readOnly}
          onRemove={(field, role) => onChange(withEntry(field, role, undefined))}
        />
      ) : null}
    </div>
  );
}

//: The two counted units these sentences use, so a plural is decided once.
const POSITIVE_ANSWERS = ["positive answer", "positive answers"] as const;
const CRITERIA = ["criterion", "criteria"] as const;

/**
 * One count's sentence, chosen by ORIGIN rather than by whether a value is null.
 *
 * The two overall counts each have four possible origins and only two of them
 * carry a number, so the branch has to be on the origin: `?? 0` fabricated a
 * zero for an unresolved count, and `String(null)` printed the word "null" for
 * a cleared one. `unreadable` gets its own words because the document HAS a
 * value there and the engine cannot use it, which is a different thing to fix.
 */
function countSentence(
  resolved: { readonly effective: number | null; readonly origin: RuleOrigin },
  withValue: (value: number) => string,
  absent: string,
): string {
  if (resolved.origin === ORIGIN.unreadable) {
    return "This screening stores a value here that is not a whole number, so the clinical engine would fail rather than use it.";
  }
  if (resolved.origin === ORIGIN.unresolved || resolved.effective === null) {
    return absent;
  }
  return withValue(resolved.effective);
}

/** `authored ? "" : " — inherited from the base pack"`, and the engine's case. */
function originSuffix(origin: RuleOrigin): string {
  if (origin === ORIGIN.authored) {
    return ", set here";
  }
  if (origin === ORIGIN.inherited) {
    return ", inherited from the base pack";
  }
  if (origin === ORIGIN.unresolved || origin === ORIGIN.unreadable) {
    // Reached only by a caller that has already chosen its own sentence for
    // these; the suffix form would read as a claim.
    return "";
  }
  return ", the clinical engine's own default";
}

/**
 * What clearing this control leaves behind, stated from the resolved layers.
 *
 * `null` where there is nothing to say — the control is already empty. The
 * sentence is built from `inherited` and the engine default rather than from an
 * assumption that clearing removes a requirement, which is the specific error
 * the old "Leave empty for no per-step requirement" made.
 */
function clearingNote(
  authored: boolean,
  inherited: number | null,
  engineDefault: number | null,
  unit: readonly [singular: string, plural: string],
  inheritedUnreadable = false,
): string | null {
  if (!authored) {
    return null;
  }
  if (inheritedUnreadable) {
    // PRESENT AND UNUSABLE IS NOT ABSENT. Clearing restores the base pack's own
    // value, and the clinical engine raises on it — promising the engine's
    // default here would send a clinician to a broken screening.
    return "The base pack sets a value here that the clinical engine cannot use, so clearing this would leave the screening unable to decide.";
  }
  if (inherited !== null) {
    return `Clearing this restores the base pack's ${counted(inherited, unit)}.`;
  }
  if (engineDefault === null) {
    return "Clearing this leaves the value unset, and the base pack supplies none.";
  }
  return `The base pack sets none, so clearing this leaves the clinical engine's default of ${counted(engineDefault, unit)}.`;
}

/**
 * `1 positive answer`, `2 positive answers`.
 *
 * A count in a clinical sentence, so it agrees: "restores the base pack's 1
 * positive answers" reads as a typo in a document a clinician is deciding
 * against, and every one of these numbers is routinely 0 or 1.
 */
function counted(
  value: number,
  [singular, plural]: readonly [string, string],
): string {
  return `${String(value)} ${value === 1 ? singular : plural}`;
}

/**
 * What could not be resolved, said once at the top rather than per row.
 *
 * NEVER SILENT. The distinction between "not read yet" and "read, and could not
 * be resolved" is carried through, because a form that showed the second as the
 * first would look like it was still loading forever.
 */
function ContextNotice({
  context,
  pending,
  failed,
  onRetry,
  criteriaCount,
}: {
  context: ScreeningRuleContext;
  pending: boolean;
  failed: boolean;
  onRetry?: () => void;
  criteriaCount: number | null;
}) {
  if (failed) {
    // THE READ, NOT THE CONTENT. Named as a failed read and offered a retry
    // where one can help — the shape `PartialDataNotice` uses for the part
    // manifest one screen up, for the same reason.
    return (
      <Notice tone="warning">
        <span>
          The Editor Service did not answer what these rules do, so nothing
          below states an effect. The stored values are unchanged.
        </span>
        {onRetry ? (
          <Button
            variant="outline"
            size="sm"
            // LOGICAL, not physical: these forms carry Hebrew content and must
            // sit correctly under `dir="rtl"` without a second stylesheet.
            className="ms-3 align-middle"
            onClick={onRetry}
          >
            Try again
          </Button>
        ) : null}
      </Notice>
    );
  }
  if (pending) {
    return (
      <Notice tone="muted">
        The classification schema for this screening has not been read yet, so
        the effect of each rule is not shown.
      </Notice>
    );
  }
  if (context.criteria === null) {
    if (context.criteriaRefusal === "registry_shadowed") {
      // A DIFFERENT PROBLEM WITH A DIFFERENT FIX. Nothing is missing here: two
      // schemas exist and the engine keeps one, so the answer is to change a
      // coordinate rather than to mount something.
      return (
        <Notice tone="warning">
          {"Another Pathway in this library emits its classification schema under the same name as this one, and the clinical engine keeps only one of them — which one depends on what a project adopts. Until one of the two is given a coordinate whose own name differs, no rule here can be checked, and the Editor Service refuses a new one."}
        </Notice>
      );
    }
    return (
      <Notice tone="warning">
        {`The diagnostic criteria for this screening could not be resolved (${refusalWords(context.criteriaRefusal)}). The rules below are shown as stored; what they would do at the clinical engine is not known, and nothing here has been verified against it.`}
      </Notice>
    );
  }
  if (context.criteriaPartialRefusal !== null) {
    return (
      <Notice tone="warning">
        {`The base pack could not be read (${refusalWords(context.criteriaPartialRefusal)}), so this shows only the ${String(criteriaCount ?? 0)} criteria this version declares. The base pack may declare more, and inherited values are not shown.`}
      </Notice>
    );
  }
  if (context.inherited === null && context.inheritedRefusal !== null) {
    // ONLY `not_supplied` PROVES NOTHING IS INHERITED. Every other refusal means
    // the document that would have supplied a value was not read, and promising
    // the engine's default there contradicts the rows below — which withhold
    // exactly that claim. This banner used to make it for all four.
    if (context.inheritedRefusal === "not_supplied") {
      return (
        <Notice tone="muted">
          {"The base pack ships no diagnosis at this screening's coordinate, so nothing is inherited: an empty control falls to the clinical engine's own default."}
        </Notice>
      );
    }
    return (
      <Notice tone="warning">
        {`The base pack's own diagnosis for this screening could not be read (${refusalWords(context.inheritedRefusal)}). What an empty control resolves to is therefore not known, and no rule below claims one.`}
      </Notice>
    );
  }
  return null;
}

/** Server-owned words for each refusal. Never the raw enum. */
function refusalWords(refusal: string | null): string {
  switch (refusal) {
    case "base_unavailable":
      return "this deployment has no base pack mounted";
    case "coordinate_unresolved":
      return "this asset occupies no pack coordinate this service can resolve";
    case "not_supplied":
      return "the base pack ships none at this coordinate";
    case "unreadable":
      return "the document there could not be read";
    case "registry_shadowed":
      return "another Pathway emits its classification schema under the same name, and the clinical engine keeps only one of them";
    default:
      // EVERY MEMBER ABOVE, and this is what happens when one is added without
      // words: `registry_shadowed` fell through to "no reason was given", which
      // is the least useful true sentence this surface can produce.
      return "no reason was given";
  }
}

/**
 * The sentence that separates the two vocabularies.
 *
 * THE WHOLE POINT OF THE TICKET, in one place a clinician reads before touching
 * a control: the conversation asks N questions and the classifier produces M
 * criteria, and only the criteria can carry a rule.
 */
function VocabularyNote({
  criteria,
  declaredStepRoles,
}: {
  criteria: readonly EffectiveCriterion[] | null;
  declaredStepRoles: ReadonlySet<string> | null;
}) {
  if (criteria === null) {
    return (
      <p className="text-sm text-muted-foreground">
        These rules address the fields this screening&rsquo;s classifier
        produces, which are not the same as its conversation steps.
      </p>
    );
  }
  const conversationOnly = [...(declaredStepRoles ?? [])].filter(
    (role) => !criteria.some((criterion) => criterion.target === role),
  );
  return (
    <p className="text-sm text-muted-foreground">
      A rule addresses a field the classifier produces, not a question the
      conversation asks. This screening has {criteria.length}{" "}
      {criteria.length === 1 ? "criterion" : "criteria"}.
      {conversationOnly.length > 0
        ? ` ${conversationOnly.map(stepRoleLabel).join(", ")} ${conversationOnly.length === 1 ? "is a conversation step that is" : "are conversation steps that are"} not classified, so no rule addressed to ${conversationOnly.length === 1 ? "it" : "them"} can have any effect.`
        : ""}
    </p>
  );
}

/** One classification criterion, with its effective rule and both controls. */
function CriterionRow({
  criterion,
  inheritedIsKnown: inheritedKnown,
  authoredCount,
  authoredInclude,
  capturedByCount,
  capturedByFlag,
  readOnly,
  onCount,
  onInclude,
}: {
  criterion: EffectiveCriterion;
  /** Whether an absent authored value's fallback is a fact or a guess. */
  inheritedIsKnown: boolean;
  authoredCount: number | undefined;
  authoredInclude: boolean | undefined;
  capturedByCount: string | null;
  capturedByFlag: string | null;
  readOnly: boolean;
  onCount: (value: number | undefined) => void;
  onInclude: (value: boolean | undefined) => void;
}) {
  const warningId = useId();
  const label = stepRoleLabel(criterion.target);
  // ONLY WHERE AN ENTRY ACTUALLY EXISTS. `_validate_references` walks the
  // mapping's OWN KEYS, so a criterion with no threshold and no flag has no rule
  // to refuse however its name sorts against its neighbours — and a row that
  // warned about a collision the clinician has not authored would be permanent
  // noise on every screening that declares both `step1` and `step10`.
  const countCaptured = authoredCount !== undefined ? capturedByCount : null;
  const flagCaptured = authoredInclude !== undefined ? capturedByFlag : null;
  const captured = countCaptured ?? flagCaptured;
  const capturedField =
    countCaptured !== null ? "answer requirement" : "yes-total flag";

  const { threshold, inclusion } = criterion;
  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <Badge tone="neutral">
          {criterion.answerCount}{" "}
          {criterion.answerCount === 1 ? "answer" : "answers"}
        </Badge>
        {criterion.unreachable ? (
          // A REAL SOURCE VALUE, REPORTED AND NOT REFUSED. Both MOD and NII use
          // 999 against a one-answer criterion, deliberately, and the number
          // alone leaves a clinician to work out what it means.
          <Badge tone="warning">Can never count</Badge>
        ) : null}
        {threshold.resolvedKey !== null || inclusion.resolvedKey !== null ? (
          <span className="inline-flex items-center gap-1 text-xs text-destructive">
            <TriangleAlert aria-hidden="true" className="h-3 w-3" />
            {`The clinical engine resolves rule keys by prefix, so this criterion is served by the rule written for ${stepRoleLabel(threshold.resolvedKey ?? inclusion.resolvedKey ?? "")}.`}
          </span>
        ) : null}
        {captured !== null ? (
          // STATED, NOT ENFORCED. The service decides; this says what it will
          // decide, next to the control that causes it.
          <span
            id={warningId}
            className="inline-flex items-center gap-1 text-xs text-destructive"
          >
            <TriangleAlert aria-hidden="true" className="h-3 w-3" />
            {`The clinical engine resolves these keys by prefix, so this criterion's ${capturedField} would also be applied to ${stepRoleLabel(captured)}. The Editor Service refuses it.`}
          </span>
        ) : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <IntegerField
          label={`Positive answers required for ${label.toLowerCase()}`}
          description="How many of this criterion's answers must be positive for it to count toward the mandatory total."
          value={authoredCount}
          readOnly={readOnly}
          {...(countCaptured !== null ? { describedByExtra: warningId } : {})}
          effective={
            <EffectiveNote
              text={
                threshold.origin === ORIGIN.unreadable
                  ? "This screening stores a requirement here that is not a whole number, so the clinical engine would fail rather than use it."
                  : threshold.origin === ORIGIN.unresolved
                    ? `What the clinical engine would require for this criterion is not known: the base pack's own diagnosis could not be read, and its entries are resolved ahead of this version's. It is ${String(criterion.answerCount)} only if the base pack sets nothing for it.`
                    : `Effective: ${String(threshold.effective)} of ${String(criterion.answerCount)}${originSuffix(threshold.origin)}.${
                        threshold.origin === ORIGIN.engineDefault
                          ? " With no value set anywhere, the engine requires every answer."
                          : ""
                      }`
              }
              tone={
                threshold.origin === ORIGIN.unresolved ||
                threshold.origin === ORIGIN.unreadable
                  ? "warning"
                  : "muted"
              }
              clearing={
                inheritedKnown
                  ? clearingNote(
                      authoredCount !== undefined,
                      threshold.inherited,
                      criterion.answerCount,
                      POSITIVE_ANSWERS,
                      threshold.inheritedUnreadable,
                    )
                  : null
              }
            />
          }
          onChange={onCount}
        />
        <TriStateField
          label={`Counts toward the yes total for ${label.toLowerCase()}`}
          value={authoredInclude}
          readOnly={readOnly}
          {...(flagCaptured !== null ? { describedByExtra: warningId } : {})}
          effective={
            <EffectiveNote
              // BOTH READINGS, because the document key has both and the old
              // label named only the first. Either half can be unknown on its
              // own — a truthy flag FOUND in the layers that were read is a fact
              // whatever else is unread, while its absence is not.
              text={`${
                inclusion.origin === ORIGIN.unreadable
                  ? "This screening stores a flag here that is not a yes or a no, so the clinical engine would read it by truthiness"
                  : inclusion.countsTowardYesTotal === null
                    ? "Whether this criterion counts toward the yes total is not known: the base pack's own diagnosis could not be read, and its entries are resolved ahead of this version's"
                    : inclusion.countsTowardYesTotal
                      ? "Effective: counts toward the yes total"
                      : "Effective: excluded from the yes total"
              }, and ${
                inclusion.requiresPositiveAnswer === null
                  ? "whether it must have a positive answer is not known either"
                  : inclusion.requiresPositiveAnswer
                    ? "the screening fails outright if this criterion has no positive answer"
                    : "no separate positive-answer requirement"
              }${originSuffix(inclusion.origin)}.`}
              tone={
                inclusion.origin === ORIGIN.unresolved ||
                inclusion.origin === ORIGIN.unreadable
                  ? "warning"
                  : "muted"
              }
              clearing={
                authoredInclude === undefined || !inheritedKnown
                  ? null
                  : inclusion.inherited !== null
                    ? `Clearing this restores the base pack's ${inclusion.inherited ? "Yes" : "No"}.`
                    : "The base pack sets none, so clearing this leaves the clinical engine's default: counts toward the yes total, with no separate positive-answer requirement."
              }
            />
          }
          onChange={onInclude}
        />
      </div>
    </li>
  );
}

/**
 * One row when the criteria could not be resolved.
 *
 * The old form's row, minus every effective claim. The controls stay usable
 * because the document is still authorable and the service still decides; what
 * is gone is the sentence that told a clinician what the value would do.
 */
function UnresolvedRequirementRow({
  role,
  unknown,
  capturedByCount,
  capturedByFlag,
  count,
  include,
  readOnly,
  onCount,
  onInclude,
}: {
  role: string;
  unknown: boolean;
  capturedByCount: string | null;
  capturedByFlag: string | null;
  count: number | undefined;
  include: boolean | undefined;
  readOnly: boolean;
  onCount: (value: number | undefined) => void;
  onInclude: (value: boolean | undefined) => void;
}) {
  const warningId = useId();
  const label = stepRoleLabel(role);
  const countCaptured = count !== undefined ? capturedByCount : null;
  const flagCaptured = include !== undefined ? capturedByFlag : null;
  const captured = countCaptured ?? flagCaptured;
  const capturedField =
    countCaptured !== null ? "answer requirement" : "yes-total flag";
  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {unknown || captured !== null ? (
          <span
            id={warningId}
            className="inline-flex items-center gap-1 text-xs text-destructive"
          >
            <TriangleAlert aria-hidden="true" className="h-3 w-3" />
            {unknown
              ? "Not a step in this screening. The Editor Service refuses a rule that names it."
              : `The clinical engine resolves these keys by prefix, so this step's ${capturedField} would also be applied to ${stepRoleLabel(captured ?? "")}. The Editor Service refuses it.`}
          </span>
        ) : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <IntegerField
          label={`Positive answers required for ${label.toLowerCase()}`}
          description="How many of this step's classifier answers must be positive. An empty value is not 'no requirement' — what it resolves to could not be determined here."
          value={count}
          readOnly={readOnly}
          {...(unknown || countCaptured !== null
            ? { describedByExtra: warningId }
            : {})}
          onChange={onCount}
        />
        <TriStateField
          label={`Counts toward the yes total for ${label.toLowerCase()}`}
          value={include}
          readOnly={readOnly}
          {...(unknown || flagCaptured !== null
            ? { describedByExtra: warningId }
            : {})}
          onChange={onInclude}
        />
      </div>
    </li>
  );
}

/**
 * Stored rule entries the runtime never reads.
 *
 * PRESERVED, VISIBLE, AND NOT SILENTLY REMOVED. Removal is offered as an
 * explicit act because a clinician may want it, and it is never performed on
 * their behalf: these are values in an immutable clinical document, and this
 * form's job is to explain them rather than to tidy them away.
 *
 * The controls are read-only rather than editable. The service refuses a NEW or
 * CHANGED rule at a target the classifier cannot produce, so an editable field
 * here would offer exactly one outcome — a refusal at Save — while the value it
 * replaced was lost from the draft.
 */
function InertEntries({
  targets,
  mandatoryRoles,
  includeYeses,
  verified,
  effects,
  readOnly,
  onRemove,
}: {
  targets: readonly string[];
  mandatoryRoles: Record<string, number> | undefined;
  includeYeses: Record<string, boolean> | undefined;
  verified: boolean;
  effects: ReturnType<typeof entryEffects>;
  readOnly: boolean;
  onRemove: (
    field: "mandatory_roles" | "include_mandatory_yeses",
    role: string,
  ) => void;
}) {
  const inertKeys = new Set(
    (effects ?? [])
      .filter((effect) => effect.inert)
      .map((effect) => `${effect.field}:${effect.key}`),
  );
  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-foreground">
          Rules addressed to something else
        </h3>
        <p className="text-sm text-muted-foreground">
          {verified
            ? "These are stored in this version and address a name the classifier does not produce. They are kept exactly as stored; the clinical engine never reads them."
            : "These address a name that is not among the criteria this browser could resolve. Whether the clinical engine reads them could not be checked here."}
        </p>
      </div>
      <ul className="space-y-2">
        {targets.map((role) => (
          <li
            key={role}
            className="rounded-md border border-dashed border-border bg-muted/40 p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                {stepRoleLabel(role)}
              </span>
              {verified &&
              (inertKeys.has(`mandatory_roles:${role}`) ||
                inertKeys.has(`include_mandatory_yeses:${role}`)) ? (
                <Badge tone="warning">No effect</Badge>
              ) : (
                <Badge tone="neutral">Not verified</Badge>
              )}
            </div>
            <ul className="mt-2 space-y-2">
              {(
                [
                  ["mandatory_roles", mandatoryRoles?.[role]],
                  ["include_mandatory_yeses", includeYeses?.[role]],
                ] as const
              )
                .filter(([, value]) => value !== undefined)
                .map(([field, value]) => (
                  <li
                    key={field}
                    className="flex flex-wrap items-center justify-between gap-2 text-sm"
                  >
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Info aria-hidden="true" className="h-3 w-3" />
                      {field === "mandatory_roles"
                        ? `Positive answers required: ${String(value)}`
                        : `Counts toward the yes total: ${value === true ? "Yes" : "No"}`}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={readOnly}
                      onClick={() => onRemove(field, role)}
                    >
                      {`Remove this ${field === "mandatory_roles" ? "answer requirement" : "yes-total flag"}`}
                    </Button>
                  </li>
                ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The one-line effective statement under a control. */
function EffectiveNote({
  text,
  tone,
  clearing,
  unavailable = false,
}: {
  text: string;
  tone: "muted" | "warning";
  clearing: string | null;
  unavailable?: boolean;
}) {
  if (unavailable) {
    return null;
  }
  return (
    <p
      className={
        tone === "warning"
          ? "text-xs text-destructive"
          : "text-xs text-muted-foreground"
      }
    >
      {text}
      {clearing !== null ? ` ${clearing}` : ""}
    </p>
  );
}

function Notice({
  tone,
  children,
}: {
  tone: "muted" | "warning";
  children: React.ReactNode;
}) {
  return (
    <p
      className={
        tone === "warning"
          ? "rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          : "rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground"
      }
    >
      {children}
    </p>
  );
}

/**
 * An integer control whose EMPTY state is a real answer.
 *
 * `type="number"` with `inputMode="numeric"`: the value reaching the service is
 * a JSON number, because the schema is strict on purpose — `yes_count: "0"`
 * validates in a lax reader and then reaches `total_yes >= yes_count` as a
 * str-vs-int comparison that raises. `valueAsNumber` is deliberately not used:
 * it reports `NaN` for a partially typed value the same way it does for an
 * empty one, which would make "not set" and "mid-edit" indistinguishable.
 */
function IntegerField({
  label,
  description,
  value,
  required = false,
  readOnly,
  describedByExtra,
  effective,
  onChange,
}: {
  label: string;
  description?: string;
  value: number | undefined;
  required?: boolean;
  readOnly: boolean;
  describedByExtra?: string;
  effective?: React.ReactNode;
  onChange: (value: number | undefined) => void;
}) {
  const errorId = useId();
  /**
   * What was TYPED, while it differs from what the document can hold.
   *
   * `null` means "show the document's value", which is the normal case. It
   * diverges only for an integer a double cannot carry: that value is refused
   * rather than propagated, so without this the digit would vanish as it was
   * typed, which reads as a broken keyboard rather than as a refusal.
   */
  const [typed, setTyped] = useState<string | null>(null);
  const shown = typed ?? (value === undefined ? "" : String(value));

  // A THRESHOLD HAS NO UPPER BOUND ON THE SERVER, deliberately — `999` means
  // "this criterion can never count" and a larger sentinel is equally valid —
  // but a JavaScript number is a double, so past `Number.MAX_SAFE_INTEGER` the
  // value that would reach storage is not the one that was typed. It is
  // therefore KEPT OUT OF THE DOCUMENT entirely rather than written and rounded.
  //
  // TWO DIFFERENT REASONS A TYPED VALUE IS HELD BACK, and they need different
  // sentences. `Number.isSafeInteger` is false for `2.5` and for `1e21` alike,
  // so testing it alone told a clinician who typed a decimal that their number
  // was above 9007199254740991.
  const held = typed !== null && typed.trim() !== "";
  const heldNumber = held ? Number(typed) : Number.NaN;
  const notWholeOrNegative =
    held && (!Number.isInteger(heldNumber) || heldNumber < 0);
  const unrepresentable =
    held && !notWholeOrNegative && !Number.isSafeInteger(heldNumber);
  const invalid = value !== undefined && (!Number.isInteger(value) || value < 0);
  const missing = required && value === undefined && typed === null;
  const message = unrepresentable
    ? `This editor cannot carry a whole number above ${String(Number.MAX_SAFE_INTEGER)}, so this value was not applied.`
    : notWholeOrNegative
      ? // HELD, NOT STORED — so the sentence has to say so. A value on this
        // branch was withheld from the document by the `onChange` guard, and the
        // identical string is used below for a value that really did reach the
        // draft; without the second clause the two states read the same while
        // the field shows something the version will not carry.
        "Enter a whole number of zero or more, so this value was not applied."
      : missing
        ? "This value is required."
        : invalid
          ? "Enter a whole number of zero or more."
          : null;

  return (
    <Field label={label} {...(description ? { description } : {})}>
      {({ inputId, describedBy }) => (
        <>
          <Input
            id={inputId}
            type="number"
            inputMode="numeric"
            step={1}
            min={0}
            readOnly={readOnly}
            aria-disabled={readOnly || undefined}
            aria-invalid={message !== null || undefined}
            aria-describedby={joinDescriptionIds(
              describedBy,
              describedByExtra,
              message !== null ? errorId : undefined,
            )}
            value={shown}
            onChange={(event) => {
              const text = event.target.value;
              if (text.trim() === "") {
                setTyped(null);
                onChange(undefined);
                return;
              }
              const parsed = Number(text);
              if (!Number.isSafeInteger(parsed)) {
                // Held locally and NOT propagated: the document keeps the last
                // value it could carry exactly.
                setTyped(text);
                return;
              }
              setTyped(null);
              onChange(parsed);
            }}
          />
          {message !== null ? (
            <FieldError id={errorId} message={message} />
          ) : null}
          {effective}
        </>
      )}
    </Field>
  );
}

/**
 * A boolean whose absence is a third answer.
 *
 * A `Select` rather than a checkbox, because "not set" and "No" mean opposite
 * things when the inherited value is `True` — which is exactly base canon's
 * PTSD `step1`/`step2` — and a two-state control cannot say which one the author
 * meant. The vocabulary is closed and short, which is what `Select` is for.
 */
function TriStateField({
  label,
  value,
  readOnly,
  describedByExtra,
  effective,
  onChange,
}: {
  label: string;
  value: boolean | undefined;
  readOnly: boolean;
  describedByExtra?: string;
  effective?: React.ReactNode;
  onChange: (value: boolean | undefined) => void;
}) {
  const inputId = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId}>{label}</Label>
      <Select
        id={inputId}
        disabled={readOnly}
        aria-disabled={readOnly || undefined}
        {...(describedByExtra ? { "aria-describedby": describedByExtra } : {})}
        value={value === undefined ? "" : value ? "true" : "false"}
        onChange={(event) => {
          const next = event.target.value;
          onChange(next === "" ? undefined : next === "true");
        }}
      >
        <option value="">Not set — inherit</option>
        <option value="true">Yes — and require a positive answer</option>
        <option value="false">No — exclude from the yes total</option>
      </Select>
      {effective}
    </div>
  );
}

/** `step3` → "Step 3"; anything else is shown as stored. */
export function stepRoleLabel(role: string): string {
  const match = /^step(\d+)$/.exec(role);
  return match ? `Step ${match[1]}` : role;
}

/** Step order, with non-`stepN` names last and alphabetical among themselves. */
function compareStepRoles(a: string, b: string): number {
  const left = /^step(\d+)$/.exec(a);
  const right = /^step(\d+)$/.exec(b);
  if (left && right) {
    return Number(left[1]) - Number(right[1]);
  }
  if (left) return -1;
  if (right) return 1;
  return a.localeCompare(b);
}
